import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { startAppListening } from '../../middlewares/listener.middleware';
import { Step, StepObject } from '../../types/updateApplication';

type UpdateApplicationState = {
  steps: StepObject[];
  currentStepIndex: number;
  stepStartTimestamp: string;
  clientId: number | undefined;
  applicationUuid: string | undefined;
  qcode: string | undefined;
};

export const updateApplicationInitialState: UpdateApplicationState = {
  steps: [],
  currentStepIndex: -1,
  stepStartTimestamp: new Date().toString(),
  clientId: undefined,
  applicationUuid: undefined,
  qcode: undefined,
};

const updateApplicationSlice = createSlice({
  name: 'updateApplication',
  initialState: updateApplicationInitialState,
  reducers: {
    goToNextStep: (state) => {
      const nextIdx = state.steps.findIndex(
        (s, i) => i > state.currentStepIndex && !s.finished
      );
      state.currentStepIndex = nextIdx === -1 ? state.steps.length : nextIdx;
    },
    goToPreviousStep: (state) => {
      for (let i = state.currentStepIndex - 1; i >= 0; i--) {
        if (!state.steps[i].finished) {
          state.currentStepIndex = i;
          return;
        }
      }
      state.currentStepIndex = -1;
    },
    addSteps: (state, action: PayloadAction<StepObject[]>) => {
      state.steps = action.payload;
    },
    setCurrentStepIndex: (state, action: PayloadAction<number>) => {
      state.currentStepIndex = action.payload;
    },
    setStepStartTimestamp: (state) => {
      // Not sure if this is needed (same pattern observed in other slices though)
      state.stepStartTimestamp = new Date().toString();
    },
    setClientId: (state, action: PayloadAction<number>) => {
      state.clientId = action.payload;
    },
    setApplicationUuid: (state, action: PayloadAction<string>) => {
      state.applicationUuid = action.payload;
    },
    setQCode: (state, action: PayloadAction<string>) => {
      state.qcode = action.payload;
    },
    finishStep: (state, action: PayloadAction<Step>) => {
      const step = state.steps.find((s) => s.name === action.payload);
      if (step) {
        step.finished = true;
        const nextIdx = state.steps.findIndex((s) => !s.finished);
        state.currentStepIndex = nextIdx === -1 ? state.steps.length : nextIdx;
      }
    },
  },
});

export const {
  goToNextStep,
  goToPreviousStep,
  addSteps,
  setClientId,
  setApplicationUuid,
  setQCode,
  setCurrentStepIndex,
  finishStep,
} = updateApplicationSlice.actions;

export default updateApplicationSlice.reducer;

startAppListening({
  matcher: isAnyOf(goToNextStep, goToPreviousStep, finishStep),
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(
      updateApplicationSlice.actions.setStepStartTimestamp()
    );
  },
});
